const UNITS: Record<string, number> = {
  sıfır: 0,
  bir: 1,
  iki: 2,
  üç: 3,
  dört: 4,
  beş: 5,
  altı: 6,
  yedi: 7,
  sekiz: 8,
  dokuz: 9,
};
const TENS: Record<string, number> = {
  on: 10,
  yirmi: 20,
  otuz: 30,
  kırk: 40,
  elli: 50,
  altmış: 60,
  yetmiş: 70,
  seksen: 80,
  doksan: 90,
};
const NUMBER_WORDS = [...Object.keys(UNITS), ...Object.keys(TENS), 'yüz', 'bin', 'milyon'];
const TOKEN = `(?:\\(?\\d+\\)?|${NUMBER_WORDS.join('|').replace(/ı/g, '[ıI]').replace(/i/g, '[iİ]')})(?![\\p{L}\\p{N}])`;
const NUMBER_RUN = new RegExp(
  `(?<![\\p{L}\\p{N}])\\+?${TOKEN}(?:(?:[\\s,()]+|(?<!-)-(?!-)|\\.(?=\\d))${TOKEN})*`,
  'giu'
);

function spokenDigits(run: string): string | null {
  const tokens = run.toLocaleLowerCase('tr-TR').match(/[\p{L}\d]+/gu) ?? [];
  let digits = '';
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i]!;
    if (/^\d+$/.test(token)) {
      digits += token;
      continue;
    }
    if (token === 'bin' || token === 'milyon') return null;
    let value = 0;
    if (token === 'yüz') {
      value = 100;
    } else if (UNITS[token] > 0 && tokens[i + 1] === 'yüz') {
      value = UNITS[token]! * 100;
      i++;
    } else if (TENS[token]) {
      value = TENS[token]!;
    } else {
      digits += UNITS[token];
      continue;
    }
    if (value >= 100 && TENS[tokens[i + 1]!]) value += TENS[tokens[++i]!]!;
    if (UNITS[tokens[i + 1]!] > 0) value += UNITS[tokens[++i]!]!;
    digits += value;
  }
  return digits;
}

/** Display-only: preserve the original messages used by booking and OTP. */
export function formatPhoneText(text: string): string {
  const phoneContext = /telefon|numara|cep/iu.test(text);
  const phoneCorrection = phoneContext && /değil|yanlış|düzelt|olacak/iu.test(text);
  const formatRun = (run: string): string => {
    // A numeric date plus the next hour can have exactly as many digits as a phone.
    if (/\d{1,2}([.-])\d{1,2}\1\d{4}/.test(run)) return run;
    let digits = spokenDigits(run);
    if (!digits) return run;
    if (phoneContext && /^0?5\d{2}$/.test(digits)) return digits;
    if (/^90(?:[2345]\d{2}|8[05]0)\d{7}$/.test(digits)) digits = `0${digits.slice(2)}`;
    if (/^(?:[2345]\d{2}|8[05]0)\d{7}$/.test(digits)) digits = `0${digits}`;
    if (/^444\d{4}$/.test(digits))
      return `${digits.slice(0, 3)} ${digits.slice(3, 4)} ${digits.slice(4)}`;
    if (!/^0(?:[2345]\d{2}|8[05]0)\d{7}$/.test(digits)) return run;
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9)}`;
  };
  return text.replace(NUMBER_RUN, (run) => {
    const formatted = formatRun(run);
    // Partial corrections are not complete phone numbers. Keep the caller's
    // separators and leading zeros without filling in any missing digits.
    if (formatted === run && phoneCorrection && /[\p{L}]/u.test(run)) {
      const tokens = run.match(/[\p{L}\d]+/gu) ?? [];
      if (tokens.length > 1) {
        const digits = spokenDigits(run);
        if (digits && digits.length <= 7) {
          return run
            .split(/(,\s*)/)
            .map((part) => spokenDigits(part) || part)
            .join('');
        }
      }
    }
    // A comma can separate phone groups or end the phone before a date/count.
    return formatted === run && run.includes(',')
      ? run
          .split(/(,\s*)/)
          .map(formatRun)
          .join('')
      : formatted;
  });
}
